// cell renderers and other 'framework components' to be used in ag-grid quote sheet
// assume they will all get a prop of 'params'
/*
inside the parent component that uses these, you should pass in the parent component instance so these components can access it:
    this.gridOptions = {
        context: {
            componentParent: this,
        },
        ... and more

then these child 'framework components' can access 'parent' component via this.params.context.componentParent
*/

/*
when using these, you need to:
0) Write them with Vue.extend({}) syntax instead of component!  then you don't have to declare them in components: {} option, avoiding any linting errors
1) import them in the file where you're using ag-grid
2) DO NOT declare them in components: {} option -- since you're using Vue.extend() instead
3) register them in this.gridOptions.frameworkComponents (someComponentName: SomeComponentName)
4) use it somewhere, like in columnDefs.cellRenderer = 'someComponentName'   (note cellRenderer instead of just cellRendererFramework, since you're putting them in frameworkComponents)
... if you didn't use frameworkComponents, you could use them directly (as STRINGS) like cellRendererFramework: 'MyComponent' -- but then you run into linting problems with unused components
*/

/* params available in components:
    value: any, // value to be rendered
    valueFormatted: any, // value to be rendered formatted
    getValue: () => any, // convenience function to get most recent up to date value
    setValue: (value: any) => void, // convenience to set the value
    formatValue: (value: any) => any, // convenience to format a value using the column's formatter
    data: any, // the row's data
    node: RowNode, // row node
    colDef: ColDef, // the cell's column definition
    column: Column, // the cell's column
    rowIndex: number, // the current index of the row (this changes after filter and sort)
    api: GridApi, // the grid API
    eGridCell: HTMLElement, // the grid's cell, a DOM div element
    eParentOfValue: HTMLElement, // the parent DOM item for the cell renderer, same as eGridCell unless using checkbox selection
    columnApi: ColumnApi, // grid column API
    context: any, // the grid's context
    refreshCell: () => void // convenience function to refresh the cell
*/

import Vue from 'vue';
import { Duration } from 'luxon';
import isFinite from 'lodash/isFinite';
import { hasValueOrZero, toNumberOrNull } from '../../js/filters-helpers';

var QuoteLineCheckboxCellRenderer = Vue.extend({
    // can take extra params:
    //  - disabled: boolean -- if true, it disables the checkbox
    data: function() { 
        return {
            isDisabled: false,  // set in refresh() method
        } 
    },
    mounted: function() {
        this.setData(this.params);
    },
    methods: {
        refresh(params) {
            // set local data, return boolean where true = reuse this component, false = make new?
            this.setData(params);
            // return false; // ?
        },
        setData(params) {
            // NOTE - this is getting called continuously during row drags... maybe you should not?!
            // console.log(params);
            this.isDisabled = (params.disabled === true) ? true : false;  // check for explicit true, then set accordingly
        },
        checkedHandler(event) {
            // no longer using 'on'/'off' strings - using valueFormatter, valueParser instead
            let checked = event.target.checked;
            //let onOff = checked ? 'on' : 'off';
            let colId = this.params.column.colId;
            //this.params.node.setDataValue(colId, onOff);
            this.params.node.setDataValue(colId, checked);
        }
    },
    // old part of template:  :checked="params.value === 'on'" --> changed to :checked="params.value"
    // updated April 3 2021 - removed v-if on the input: v-if="params.node.data.isVariant"
    template: `
        <input 
            type="checkbox" 
            :disabled="isDisabled"
            @click="checkedHandler($event)"
            style="height: 16px; width: 16px;"
            :checked="params.value"
        />
    `
})

var QuoteSheetItemRendererWithChoices = Vue.extend({
    // use cellRendererSelector function on parent component instead of all the conditionals here?
    // OLD VERSION -- with buttons to choose manual entry or saved item... but better to not let that choice happen here...
    // user should make choice when inserting a new thing... either add new item (manual entry) or add saved item
    // otherwise we run into problems adding a saved item to an existing manual entry row... more room for errors
    data: function() {
        return {}
    },
    computed: {},
    methods: {
        startEditing: function() {
            // tell gridApi to start editing this cell
            let thisCellParams = {
                rowIndex: this.params.rowIndex,
                colKey: this.params.column.getId(),
            };
            console.log('startEditing, thisCellParams:');
            console.log(thisCellParams);
            this.params.api.startEditingCell(thisCellParams);
        },
        showItemPicker: function() {
            // show the 'add saved item' modal on parent
            this.params.context.componentParent.showItemPicker();
        },
    },
    template: `
        <div v-if="params.node.level === 0" key="toplevel" class="w-100">
            <div v-if="params.value" key="hasItemName" class="font-weight-bold">
                {{ params.value }}
                <b-icon v-if="params.data.hasLinkedProduct" icon="box" variant="primary" class="ml-2"></b-icon>
            </div>
            <div v-else key="noItemValue" class="d-flex justify-content-around align-items-center w-100 font-size-70">
                <b-button @click="startEditing()" size="sm" variant="sharp-primary" class="font-size-70 py-0 px-1">+ New Item</b-button>
                <div class="text-nowrap font-size-70 mx-1">OR</div>
                <b-button @click="showItemPicker" size="sm" variant="sharp-info" class="font-size-70 py-0 px-1">+ Saved Item</b-button>
            </div>
        </div>
        <div v-else key="variant" class="text-muted font-size-70 w-100 d-flex justify-content-end">
            VARIANT &rarr;
        </div>
    `
})

var QuoteSheetItemRenderer = Vue.extend({
    // use cellRendererSelector function on parent component instead of all the conditionals here?
    data: function() {
        return {}
    },
    computed: {},
    methods: {
        startEditing: function() {
            // tell gridApi to start editing this cell
            let thisCellParams = {
                rowIndex: this.params.rowIndex,
                colKey: this.params.column.getId(),
            };
            console.log('startEditing, thisCellParams:');
            console.log(thisCellParams);
            this.params.api.startEditingCell(thisCellParams);
        },
        showItemPicker: function() {
            // show the 'add saved item' modal on parent
            this.params.context.componentParent.showItemPicker();
        },
    },
    template: `
        <div v-if="params.node.level === 0" key="toplevel" class="w-100 py-2">
            <div v-if="params.value" key="hasItemName" class="font-weight-bold">
                {{ params.value }}
                <b-icon v-if="params.data.hasLinkedProduct" icon="box" variant="primary" class="ml-2"></b-icon>
            </div>
            <div v-else key="noItemValue" style="color: var(--gray-400)">
                Item Name
            </div>
        </div>
        <div v-else key="variant" class="text-muted font-size-70 w-100 d-flex justify-content-end">
            VARIANT &rarr;
        </div>
    `
})


var QuoteSheetDescriptionRenderer = Vue.extend({
    data: function() {
        return {
        }
    },
    computed: {},
    methods: {},
    template: `
        <div v-if="params.value" key="desc_value">{{ params.value }}</div>
        <div v-else key="desc_placeholder" class="grid-placeholder-text">
            {{ params.node.level === 0 ? 'Item Description' : 'Variant Description' }}
        </div>
    `
})

// VUE VERSION
/* var SimplePlaceholderRenderer = Vue.extend({  // use this in colDefs --> cellRendererParams: {placeholder: 'Some Placeholder'}
    data: function() {
        return {}
    },
    template: `
        <span :style="params.value ? '' : 'color: var(--gray-400);'">{{ params.value || params.placeholder }}</span>
    `
}) */   

// SIMPLE FUNCTION VERSION (less good... should not be used when you need complex cell refresh, but works for most things
// should return string of HTML or DOM object
// from docs:  Use the function variant of a cell renderer if you have no refresh or cleanup requirements (ie you don't need to implement the refresh or destroy functions)
var SimplePlaceholderRenderer = function(params) {  // NON-VUE VERSION (faster) - use this in colDefs --> cellRendererParams: {placeholder: 'Some Placeholder'}
    // THIS COUNTS ZERO 0 as a valid value, will not show placeholder -- if you want to show placeholder, value should be empty string
    // wrap this in a span with class like 'simple-placeholder' and/or 'placeholder-colId' to make css easier?
    if (!params.column) return '';
    let hasValue = hasValueOrZero(params.value);
    let value = hasValue ? (params.valueFormatted || params.value) : params.placeholder
    let cellDiv = document.createElement('div');
    // only add grid-placeholder-text class if its showing the placeholder
    cellDiv.innerHTML = `<span class="${hasValue ? '' : 'grid-placeholder-text '}simple-placeholder placeholder-col-${params.column.colId}">` + value + `</span>`;
    return cellDiv;
};


var ActionButtonComponent = Vue.extend({  
    // can access your main component holding the grid using this.params.context.componentParent
    data: function() {
        return {
            showConfirmDelete: false,  // toggle to show/hide confirmDelete
            isOpen: false,  // using .sync with open prop on v-popover
        }
    },
    computed: {
    },
    methods: {
        deleteNode: function() {
            // perform the delete -- SHOW CONFIRMATION TO USER BEFORE DOING THIS
            // works with any type of row... if parent node, it deletes all variants also!
            this.params.context.componentParent.deleteNode(this.params.node);
            this.closeAndReset();
        },
        cancelDelete: function() {  // just close and reset
            this.closeAndReset();
        },
        cloneNode: function() {
            // TODO -- add args, or separate method for cloning parent vs. variant 
            this.params.context.componentParent.cloneNode(this.params.node);
            this.closeAndReset();
        },
        addVariant: function() {
            // this works with any type of row -- combined, variant, or parent, 
            // but you may still want to disable for some
            this.params.context.componentParent.addVariant(this.params.node);
            this.closeAndReset();
        },
        saveItem: function() {
            // save this row to the sourcing database
            console.log('Should save item to DB');
            this.closeAndReset();
        },
        emitAndClose: function(eventName) {
            // generic helper function to emit various events, then close popover
            // pass in event name such as 'clone' or 'add-variant'
            // this will add rowNode object (params.node) to emit event, then close the popover
            this.$emit(eventName, this.params.node);
            this.closeAndReset();
        },
        closeAndReset: function() {
            // DRY function to close popover & reset all things (in that order, so it doesn't flash other things before closing)
            // need to reset things because component doesn't get destroyed for some time after closing
            this.isOpen = false;
            this.showConfirmDelete = false;
        }
    },
    template: `
        <v-popover
            offset="8"
            :open.sync="isOpen"
            auto-hide
            :handleResize="false"
            placement="right"
            popover-inner-class="popover-inner"
        >  
            <!-- This will be the popover target (for the events and position) -->
            <b-link>
                <b-iconstack font-scale="1.5" class="tooltip-target">
                    <b-icon stacked icon="three-dots" scale="0.75" class=""></b-icon>
                    <b-icon stacked icon="circle" class=""></b-icon>
                </b-iconstack>
            </b-link>
        
            <!-- This will be the content of the popover -->
            <template slot="popover" class="p-0">
                <div v-if="showConfirmDelete" key="confirmDelete" class="py-2 px-3">
                    <div class="font-weight-bold mb-1 text-center">Delete Row</div>
                    <div class="mb-2 text-center">
                        <div>Are you sure?</div>
                        <div v-if="params.node.allChildrenCount" class="font-weight-bold mt-1">This will also delete all item variants.</div>
                    </div>
                    <b-button size="sm" block variant="sharp-secondary" @click="cancelDelete">No, Cancel</b-button>
                    <b-button size="sm" block variant="danger" @click="deleteNode">Yes, Delete</b-button>
                </div>
                <div v-else class="d-flex flex-column">
                    <a @click="cloneNode" href="#" class="dropdown-item">Clone</a>
                    <a @click="addVariant" href="#" class="dropdown-item">Add Variant</a>
                    <a v-if="params.data.availableToSave" @click="saveItem" href="#" class="dropdown-item">Save to Sourcing Database</a>
                    <!-- <a @click="emitAndClose('add-above')" href="#" class="dropdown-item">Add Above</a>
                    <a @click="emitAndClose('add-below')" href="#" class="dropdown-item">Add Below</a> -->
                    <a @click="showConfirmDelete = true" href="#" class="dropdown-item">Delete</a>
                </div>
            </template>
        </v-popover>
    `
});


var SavingOverlayComponent = Vue.extend({
    template: `
        <div class="ag-overlay-loading-center font-weight-bold" style="background-color: var(--primary-100);">
            {{ params.loadingMessage }}
        </div>
      `,
    data: function () {
      return {};
    },
    beforeMount() {},
    mounted() {},
    methods: {},
  });


///////////////////////////////////////
/////  QuoteSheetReply components /////

var DisabledVendorInputRenderer = Vue.extend({
    template: `
        <div class="w-100 text-center text-gray-400">{{ emptyValue }}</div>
    `,
    data: function() {
        return {
            emptyValue: '---',
        }
    }
});

// simpler version -- not a Vue component, but just a function -- it gets wrapped in a <span> just like normal string values
var SimpleDisabledInputRenderer = function() { // this will never show params.value -- only use it when not showing value for entire row, or in cellRendererSelector
    return '---';
}

var NoBidInputRenderer = function() {  // use cellRendererSelector to show this in cells when the vendor selected 'No Bid' on this line
    return '';
}

var PriceCellRenderer = function(params) {  // for showing generic prices
    let currencyCode = (params.data ? params.data.bid.price_currency : params.context.currency) || 'USD';
    const placeholder = currencyCode;  // just use the currency code for generic version of this renderer
    console.log(`PriceCellRenderer - params.value, then params.valueFormatted, then params.data`, params.value, params.valueFormatted, params.data);
    //return Number.isFinite(params.value) ? params.valueFormatted : currencyCode;  // Number.isFinite(0) --> true
    return hasValueOrZero(toNumberOrNull(params.value)) ? params.valueFormatted : placeholder;
    // above does this: see if we have a valid price, allowing zero (hasValueOrZero is a boolean) -- if we have a valid price, return the formatted version of it.  otherwise just show the currency code
};

var VendorResponseHeaderRenderer = Vue.extend({
    template: `
        <div class="ag-header-group-cell-label vendor-response-area-header">
            <div class="ag-header-group-text d-block">
                <div class="vendor-response-area-heading">{{ params.displayName }}</div>
                <div class="vendor-response-area-subheading">{{ params.subheading }}</div>
            </div>
        </div>
    `,
    data: function() {
        return {}
    },
    methods: {},
});


var GenericBooleanRenderer = Vue.extend({
    template: `
        <b-icon 
            :icon="params.value ? 'check-circle-fill' : 'exclamation-circle-fill'"
            :variant="params.value ? 'success' : 'danger'"
        ></b-icon>
    `,
    data: function() {
        return {}
    }
})

//const validSnippet = `<span class="text-success">&#10004;</span>`; // '&#10004;'  ${validSnippet}
//const invalidSnippet = `<span class="text-danger font-size-100">&#215;</span>`;  //  '&#215;'  ${invalidSnippet}

// HTML(svg) of bootstrap icons, make it simple so we don't have to render full Vue components
const validSnippet = `
    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" class="bi bi-check-circle-fill text-success" viewBox="0 0 16 16" focusable="false" role="img">
        <path d="M16 8A8 8 0 1 1 0 8a8 8 0 0 1 16 0zm-3.97-3.03a.75.75 0 0 0-1.08.022L7.477 9.417 5.384 7.323a.75.75 0 0 0-1.06 1.06L6.97 11.03a.75.75 0 0 0 1.079-.02l3.992-4.99a.75.75 0 0 0-.01-1.05z"/>
    </svg>
`;
const invalidSnippet = `
    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" class="bi bi-x-circle-fill text-danger" viewBox="0 0 16 16" focusable="false" role="img">
        <path d="M16 8A8 8 0 1 1 0 8a8 8 0 0 1 16 0zM5.354 4.646a.5.5 0 1 0-.708.708L7.293 8l-2.647 2.646a.5.5 0 0 0 .708.708L8 8.707l2.646 2.647a.5.5 0 0 0 .708-.708L8.707 8l2.647-2.646a.5.5 0 0 0-.708-.708L8 7.293 5.354 4.646z"/>
    </svg>
`;

/* 
<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" class="bi bi-x-circle text-danger" viewBox="0 0 16 16" focusable="false" role="img">
        <path d="M8 15A7 7 0 1 1 8 1a7 7 0 0 1 0 14zm0 1A8 8 0 1 0 8 0a8 8 0 0 0 0 16z"/>
        <path d="M4.646 4.646a.5.5 0 0 1 .708 0L8 7.293l2.646-2.647a.5.5 0 0 1 .708.708L8.707 8l2.647 2.646a.5.5 0 0 1-.708.708L8 8.707l-2.646 2.647a.5.5 0 0 1-.708-.708L7.293 8 4.646 5.354a.5.5 0 0 1 0-.708z"/>
    </svg>
*/

/* var ValidationStatusRenderer = function(params) {  // for showing validation status -- params.value should be a Boolean, true = isValid
    return params.value ? validSnippet : invalidSnippet;  // value here should just be normal true/false boolean
} */

var ValidationStatusRenderer = Vue.extend({  // params.value will be a boolean
    template: `
        <div :style="{ paddingTop: params.padding, paddingBottom: params.padding }">
            <b-icon :icon="params.value ? 'check-circle-fill' : 'x-circle-fill'" :variant="params.value ? 'success' : 'danger'"></b-icon>
        </div>
    `
})


var VueDateButtonRenderer = Vue.extend({
    // USE WITH BootstrapVueDatePickerEditor -- this just shows the date and a button that triggers the edit
    // you can display either params.value or params.valueFormatted  (if you're using a valueFormatter)
    data: function() {
        return {}
    },
    methods: {
        startEditing: function() {
            this.params.api.startEditingCell({
                rowIndex: this.params.rowIndex,
                colKey: this.params.column.colId,
            });
        },
    },
    // NOTE: don't use d-flex on top-level for things in cells... so you can use d-none to hide them if needed
    template: `
    <div class="w-100">
        <div class="w-100 d-flex justify-content-between align-items-center">
            <div 
                class="flex-fill"
                :class="params.valueFormatted ? '' : 'text-gray-400'"
            >
                {{ params.valueFormatted || params.placeholder }}
            </div>
            <button 
                class="btn btn-sm btn-bare-secondary pr-0"
                @click="startEditing"
            >
                <b-icon icon="calendar3"></b-icon>
            </button>
        </div>
    </div>
    `
})

var BootstrapVueDatePickerEditor = Vue.extend({  // b-calendar version
    template: `
        <div class="datepicker-cell-wrapper">
            <b-calendar 
                ref="calComp"
                v-model="dateValue" 
                @input="setDate"
                @context="onContext" 
                locale="en-US"
            >
                <div class="d-flex" dir="ltr">
                    <b-button
                        size="sm"
                        variant="minimal-primary"
                        v-if="dateValue && !params.hideClear"
                        class="button-text"
                        @click="clearDate"
                    >
                        Clear
                    </b-button>
                </div>
            </b-calendar>
        </div>
    `,
    data() {
        return {
            emptyValue: null,  // keep it as null for empty value
            dateValue: '',  // localValue in YYYY-MM-DD format - BUT you need to actually set the data to null if its empty - backend wants that
            context: null,  // the full context object when its updated, lots of stuff
            // TODO - pass in params for allowing only FUTURE dates?
            // if so, make sure you adjust date to TENANT TZ date
            // you can pass in 'hideClear' as extra param (boolean)
        }
    },
    created() {
        this.dateValue = this.params.value || this.emptyValue;
        // for editors, just work with local data, then use this.params.api.stopEditing() to tell grid you're done, then use getValue() method to tell grid where to get value
    },
    mounted() {
        // console.log('params from bsv datepicker component', this.params);
        this.$nextTick(() => {
            const calComp = this.$refs.calComp;  
            if (calComp) {
                calComp.focus();  // might have to get its element?
            }
        });
    },
    methods: {
        getValue() {  // if this is an EDITOR, this is main method that grid uses to get the value
            //console.log('from getValue in date picker component, dateValue is', this.dateValue);
            return this.dateValue || this.emptyValue;
        },
        isPopup() {
            return true;
        },
        setDate: function(newDate) {  // set new date value and stop editing
            this.dateValue = newDate || this.emptyValue;
            this.params.api.stopEditing();  // this tells grid we're done and it gets value using getValue() - can also use this.params.stopEditing()
            // try returning focus to the grid cell
            this.params.eGridCell.focus();  // eGridCell is the ag-grid cell we're in, it is HTMLElement
        },
        onContext(ctx) {
            // handles context event, which is fired whenever something changes
            // v-model is updated separately
            this.context = ctx
        },
        clearDate() {
            this.dateValue = this.emptyValue;
        }
    }
})

// DEPRECATED Oct 3 2022 - remove in a bit
// var VueDatePickerEditor = Vue.extend({
//     template: `
//         <div class="datepicker-cell-wrapper">
//             <v-date-picker
//                 v-model="dateValue"
//                 :model-config="modelConfig"
//                 @input="setDate"
//                 v-on="{popoverDidShow: onPopoverDidShow}"
//                 ref="datePicker"
//             >
//             </v-date-picker>
//         </div>
//     `,
//     data: function() {
//         return {
//             // params - no need to declare it - can get params.value
//             dateValue: '',  // local value
//             modelConfig: {
//                 type: 'string',  // this means v-model will take and emit string-formatted dates
//                 mask: 'YYYY-MM-DD', // Uses 'iso' if missing
//             },
//             popoverConfig: {
//                 //positionFixed: true,
//                 placement: 'left-start',
//             },
//             datePickerComp: null,  // set $ref here in mounted
//         }
//     },
//     created: function() {
//         this.dateValue = this.params.value;
//         // for editors, just work with local data, then use this.params.api.stopEditing() to tell grid you're done, then use getValue() method to tell grid where to get value
//     },
//     mounted: function() {
//         //Vue.nextTick(() => {
//         this.datePickerComp = this.$refs.datePicker;  // still does not work... need nextTick?
//         /* this.$nextTick(() => {
//             let today = new Date();  // start with today's date focused so can key up/down/left/right to select, hit enter
//             this.$refs.datePicker?.focusDate(today);  // this is async, returns a Promise()
//         }); */
//     },
//     methods: {
//         getValue() {  // if this is an EDITOR, this is main method that grid uses to get the value
//             //console.log('from getValue in date picker component, dateValue is', this.dateValue);
//             return this.dateValue;
//         },
//         isPopup: function() {
//             return true;
//         },
//         setDate: function(newDate) {
//             this.dateValue = newDate;
//             this.params.api.stopEditing();  // this tells grid we're done and it gets value using getValue()
//             // try returning focus to the grid cell
//             this.params.eGridCell.focus();  // eGridCell is the ag-grid cell we're in, it is HTMLElement
//         },
//         onPopoverDidShow() {
//             console.log('POPOVER DID SHOW FOR DATEPICKER');
//             console.log(`Do we have datePickerComp? ${!!this.datePickerComp}`);
//             let today = new Date();
//             this.datePickerComp?.focusDate(today);  // returns Promise - but still doesn't highlight the date?
//         }
//     }
// })

var LeadTimeEditor = Vue.extend({
    template: `
        <div class="w-100 h-100 d-flex justify-content-center">
            <input 
                type="number" 
                :value="localValueObj.number"
                @input="updateNumber"
                @keyup.tab.stop
                @keydown.tab.stop
                step="1"
                class="h-100 border-0 py-0 pr-0 pl-1 flex-fill"
                style="width: 80px;"
                ref="numberInput"
            />
            <select 
                :value="localValueObj.period"
                @input="updatePeriod"
                class="h-100 border-0 flex-fill"
                style="width: 70px;"
                ref="periodInput"
            >
                <option 
                    v-for="period in periodOptions" 
                    :key="period"
                    :value="period"
                >{{ period }}</option>
            </select>
        </div>
    `,
    data: function() {
        return {
            // take the incoming value from params.value (ISO Duration string?) and parse it into an object {number: 5, period: 'weeks'}
            periodOptions: [  // make sure all these are exact keys used in luxon Duration.fromISO() - this is basically a constant, not dynamic
                'years', 
                'months',
                'weeks', 
                'days',
                'hours',
            ],
            // update both localValueObj and localValueIso at the same time
            localValueObj: { // set defaults in here... if incoming value (params.value) was empty or not valid, we'll start with these defaults
                number: null,
                period: 'weeks',
            },
            localValueIso: '',  // ISO 8601 duration string... this is what's reported back to grid
        }
    },
    created: function() {
        // set initial local data from this.params.value -- what format is params.value???  ISO duration string???
        // this.params.charPress is the string that started the edit, eg 'a' if letter a was pressed, or 'A' if shift + letter a
        // - only printable characters appear here
        this.setInitialValue(this.params.value, this.params.charPress);
        console.log('end of created - LeadTimeEditor - localValueObj - localValueIso');
        console.log(JSON.stringify(this.localValueObj));
        console.log(this.localValueIso);
    },
    mounted: function() {
        Vue.nextTick(() => {
            this.$refs.numberInput.focus();
        });  // must wrap it in nextTick or it doesn't focus
    },
    methods: {
        // OR... make this a renderer so its always showing, don't have to go in/out of edit mode?
        // or have 'done' button on editor?  would have to use a popup editor for that becuase no space in cell otherwise...
        getValue() {  // if this is an EDITOR, this is main method that grid uses to get the value
            return this.localValueIso;
        },
        isPopup: function() {
            return false;
        },
        setInitialValue: function(initialValue, charPress) {  // call this during created hook with setInitialValue(params.value, params.charPress) 
            // no need to return anything... just set this.localValueObj and this.localValueIso as you go
            let initialNumber = null;  // we'll set this depending if we have charPress and/or initialValue
            if (initialValue) {
                // must set this.localValueObj.period and this.localValueObj.number (via initialNumber) if we have initial value
                let luxonDur = Duration.fromISO(initialValue);
                if (luxonDur && luxonDur.isValid) {  // continuing on, assuming we have a valid luxon Duration instance
                    let durObj = luxonDur.toObject();  // make a JS object representing this duration like {weeks: 4}
                    // WHAT IF IT MAKES OBJECT WITH MORE THAN ONE KEY?  SHOULD WE FORCE MAKE OBJECT USING ONLY ONE KEY?  'weeks', 'months', etc...?
                    // worry about that later... if needed, check length of keys, and if you have more than 1, then go back and force object with only weeks or something like that
                    if ( Object.keys(durObj).length === 1 ) {  // continue on, assuming we have only one entry in durObj (like {weeks: 4})
                        let periodString = Object.keys(durObj)[0];  // something like 'weeks'
                        let number = Object.values(durObj)[0];  // get the corresponding value (# of weeks, etc.)
                        if (this.periodOptions.includes(periodString) && isFinite(number)) { // if the period isn't in our list of options, bail, and make sure we have a valid number
                            this.localValueObj.period = periodString;
                            initialNumber = number;
                        }
                    }
                }
            }
            // check/set charPress AFTER initial value, because any new charPress should take priority for setting number, and we want to keep same period if there was any initial value
            if (charPress) {  // if you have something in charPress, that means user wants to edit it, so use this value for number instead of any existing value (initialValue)
                console.log(`LeadTimeEditor - charPress: ${charPress}`);
                if (Number.isFinite(Number(charPress))) {  // check if charPress is a valid number
                    initialNumber = charPress;
                }
            }
            this.localValueObj.number = initialNumber;
            this.localValueIso = this.makeDurationString();  // returns '' if something is not valid, so should be OK to call it anytime
            // have to call this here if we had valid number,period so we don't blank existing value
        },
        makeDurationString: function() {  // returns ISO duration string using current localValueObj data, returns '' if its not a valid Duration
            // build the ISO duration string & return it - don't set it in here - return '' if we don't have enough data
            let number = this.localValueObj.number;
            let period = this.localValueObj.period;
            if (!number || !period) return '';
            let durationObj = {
                [period]: number,
            };
            console.log('makeDurationString - durationObj');
            console.log(`period: ${period}`);
            console.log(`number: ${number}`);
            console.log(JSON.stringify(durationObj));
            // convert object to luxon Duration instance
            let durationInst = Duration.fromObject(durationObj);
            // convert to ISO string, if its a valid duration
            if (durationInst.isValid) {
                return durationInst.toISO();
            } else {
                return '';
            }
        },
        updateNumber: function(event) {
            console.log('updateNumber in LeadTimeEditor - new number:');
            // new value will always be a string, maybe empty ''
            // console.log(event.target.value, typeof event.target.value);
            const newVal = toNumberOrNull(event.target.value);  // '' turns into null
            this.localValueObj.number = newVal;  // OK if its null - that was original value
            /* OLD VERSION - only set things if you have actual number 
            if (event.target.value) {
                this.localValueObj.number = event.target.value;
            } */
            this.setValue();
        },
        updatePeriod: function(event) {
            console.log('updatePeriod in LeadTimeEditor - new period:');
            console.log(event.target.value);
            if (event.target.value) {
                this.localValueObj.period = event.target.value;
            }
            this.setValue();
        },
        setValue: function() {
            // make the ISO string, set this.localValueIso if its valid, and stop editing if its valid
            this.localValueIso = this.makeDurationString();  // will return '' if not a valid / complete duration
            console.log(`LeadTimeEditor - just set ISO value to ${this.localValueIso}`);
            // tell grid we're done editing so it can get the value using getValue()
            // only do this if we have a complete/valid duration... otherwise let the user stop editing some other way (click away, etc)
            /* if (this.localValueIso) {
                Vue.nextTick(() => {
                    console.log('STOP EDITING');
                    // don't use stopEditing here... because as soon as you enter first digit, if value is valid, you'll stop editing, user can't enter second digit
                    //this.params.stopEditing();  // can either call params.stopEditing() or params.api.stopEditing() (entire grid)
                });
            } */
        },
        handleLocalTab: function(event) {  // when we hit tab on number input, this gets called
            // call event.stopPropagation() and maybe focus on other input
            event.stopPropagation();  
            // could also just use @keyup.tab.stop and @keydown.tab.stop and not call a function - this keeps the tab inside this component... focuses on other input, which is good
        }
    }
})

var GenericCheckboxCellRenderer = Vue.extend({
    data: function() { 
        return {
            isDisabled: false,  // set in refresh() method
            localChecked: undefined,  // make sure to get/set using params.getValue() and params.setValue() - normally would come from params.value 
        } 
    },
    beforeMount() {
        this.refreshData(this.params);
    },
    mounted() {
        // this.refreshData(this.params);  // do it in beforeMount instead
    },
    methods: {
        refresh(params) {
            // set local data, return boolean where true = reuse this component, false = make new?
            this.refreshData(params);
            // return false; // ?
        },
        refreshData(params) {
            //console.log(params);
            this.isDisabled = !params.colDef.editable(params);  // THIS WORKS!
            this.localChecked = params.getValue();  // don't just get params.value - but call getValue()
        },
        checkedHandler(event) {
            // no longer using 'on'/'off' strings - using valueFormatter, valueParser instead
            // only need to worry about updates that happen locally here... make sure to use node.setDataValue() so parent knows we changed
            // but for changes FROM parent, don't do this... just display updated value using refresh()/refreshData()
            let checked = event.target.checked;
            let colId = this.params.column.colId;
            this.params.node.setDataValue(colId, checked);
            // FORCE A REFRESH FOR THIS ROW -- used when this checkbox controls rendering/values of other things in this row, like for no-bid selection
            this.params.api.refreshCells({
                rowNodes: [this.params.node],
                force: true,
                suppressFlash: true,
            });
        }
    },
    template: `
        <div class="w-100 text-center">
            <input 
                type="checkbox" 
                :disabled="isDisabled"
                @click="checkedHandler($event)"
                style="height: 16px; width: 16px;"
                :checked="localChecked"
            />
        </div>
    `
})


// GENERIC HEADER CELL RENDERERS

// pass in cellRendererParams of 'padding' such as '10px' -- will get used as top and bottom padding
var DynamicPaddingRenderer = Vue.extend({
    template: `
        <div class="dynamic-padding-renderer" :style="{ paddingTop: params.padding, paddingBottom: params.padding }">
            {{ params.value }}
        </div>
    `,
})

var TitleSubtitleHeaderGroupRenderer = Vue.extend({
    template: `
        <div class="ag-header-group-cell-label title-subtitle-header">
            <div class="ag-header-group-text d-block">
                <div class="title-subtitle-header-title">{{ params.displayName }}</div>
                <div class="title-subtitle-header-subtitle text-wrap font-weight-400">{{ params.subtitle }}</div>
            </div>
        </div>
    `,
    data: function() {
        return {}
    },
    methods: {},
});



export {
    QuoteLineCheckboxCellRenderer,
    ActionButtonComponent,
    QuoteSheetItemRenderer,
    QuoteSheetItemRendererWithChoices,
    QuoteSheetDescriptionRenderer,
    SavingOverlayComponent,
    SimplePlaceholderRenderer,
    PriceCellRenderer,
    VendorResponseHeaderRenderer,
    GenericBooleanRenderer,
    ValidationStatusRenderer,
    validSnippet,
    invalidSnippet,
    DisabledVendorInputRenderer,
    NoBidInputRenderer,
    SimpleDisabledInputRenderer,
    BootstrapVueDatePickerEditor,
    // REPLACED VueDatePickerEditor,
    VueDateButtonRenderer,
    LeadTimeEditor,
    GenericCheckboxCellRenderer,
    DynamicPaddingRenderer,
    TitleSubtitleHeaderGroupRenderer,
}